/**
 * Network Config
 *
 * Connection-side timing tunables. The runtime `NetworkManager` config
 * (wsUrl / apiUrl / reconnect) lives separately on the client class — this
 * file is for shared client+server values like interpolation snap distance
 * and connection timeout.
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface NetworkConfig {
  /** Milliseconds to wait for the WebSocket handshake before giving up. */
  connectionTimeoutMs: number;
  /** Remote-entity snap threshold (shared). The per-entity-type interpolation
   *  tuning (render-lag, mode, tangent clamp) lives client-side in
   *  `src/game/systems/network/interpolationConfig.ts`. */
  interpolation: {
    /** Squared pixel distance above which we hard-snap (teleport) instead of
     *  smoothly sliding the interpolator over render-lag. */
    snapDistanceSq: number;
  };
  /** Local position-claim send cadence (client-owned movement). The client
   *  runs its own physics and throttles `PositionClaim`s to this interval; the
   *  server validates + rebroadcasts, and remote clients interpolate between
   *  claims via the SnapshotInterpolator. Lifting it here (was a buried inline
   *  constant in InputSyncSystem) makes the claim RATE the alternate lever for
   *  the remote-player-smoothness arc — DJ can tune it without a code edit. */
  positionClaim: {
    /** Throttle window in ms between position claims. 50ms = 20Hz (default).
     *  Lowering to 33ms = 30Hz sends denser data (smoother remotes) at +50%
     *  wire cost — the HIGHER-RISK smoothness lever; prefer deepening the
     *  client-side render-lag buffer (interpolationConfig.ts) first. */
    intervalMs: number;
  };
}

export const NETWORK_DEFAULTS: NetworkConfig = {
  connectionTimeoutMs: 10000,
  interpolation: {
    snapDistanceSq: 10000,
  },
  positionClaim: {
    // 20Hz — every 3rd frame at 60Hz. ALTERNATE smoothness lever (33 = 30Hz).
    intervalMs: 50,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to NETWORK_DEFAULTS.
 *
 * Examples:
 *   connectionTimeoutMs: 30000              // generous timeout for slow networks
 *   interpolation: { snapDistanceSq: 40000 } // larger teleport-snap threshold
 */
export const NETWORK_DEV_OVERRIDE: Partial<NetworkConfig> = {};

export const NETWORK: NetworkConfig = {
  connectionTimeoutMs: NETWORK_DEV_OVERRIDE.connectionTimeoutMs ?? NETWORK_DEFAULTS.connectionTimeoutMs,
  interpolation: { ...NETWORK_DEFAULTS.interpolation, ...NETWORK_DEV_OVERRIDE.interpolation },
  positionClaim: { ...NETWORK_DEFAULTS.positionClaim, ...NETWORK_DEV_OVERRIDE.positionClaim },
};
